/**
 * TextRenderer - Core component that renders analyzed text with reading aids.
 */

import PICTOGRAM_ICONS from "./PictogramIcons";

function PictogramBadge({ graphemeLabel }) {
  if (!graphemeLabel) return null;
  const picto = PICTOGRAM_ICONS[graphemeLabel];
  if (!picto) return null;
  const Icon = picto.icon;
  return (
    <span className="absolute -top-6 left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none">
      <Icon />
      <span className="text-[8px] font-nunito font-bold leading-none" style={{ color: picto.color }}>
        {picto.label}
      </span>
    </span>
  );
}

function SyllableSpan({ syl, sylIdx, color, silentSet, highlightGraphemes, wordGraphemes, showPictograms }) {
  const chars = [];
  for (let i = 0; i < syl.text.length; i++) {
    const charAbsIdx = syl.start + i;
    const isSilent = silentSet.has(charAbsIdx);
    let isHighlighted = false;
    let pictoLabel = null;

    if (wordGraphemes) {
      for (let g = 0; g < wordGraphemes.length; g++) {
        const gr = wordGraphemes[g];
        if (charAbsIdx === gr.start && gr.end - gr.start > 1) {
          if (highlightGraphemes && highlightGraphemes.has(gr.label)) {
            isHighlighted = true;
          }
          if (showPictograms && PICTOGRAM_ICONS[gr.label]) {
            pictoLabel = gr.label;
          }
        }
        if (highlightGraphemes && highlightGraphemes.has(gr.label) && charAbsIdx > gr.start && charAbsIdx < gr.end) {
          isHighlighted = true;
        }
      }
    }

    const className = (isSilent ? "silent-letter " : "") + (isHighlighted ? "grapheme-highlight" : "");
    const style = isSilent ? {} : { color };

    chars.push(
      <span key={`${sylIdx}-${i}`} className={`${className} ${pictoLabel ? "relative inline-block" : ""}`} style={style}>
        {pictoLabel && <PictogramBadge graphemeLabel={pictoLabel} />}
        {syl.text[i]}
      </span>
    );
  }

  return (
    <span className="syllable-arc" data-testid={`syllable-${sylIdx}`}>
      {chars}
    </span>
  );
}

function WordSpan({ word, syllableColors, showSilent, highlightGraphemes, nextType, showPictograms }) {
  if (word.type === "space") {
    // Don't render space before punctuation (the word before handles spacing)
    if (nextType === "punct") return null;
    return null; // Spaces are handled by trailing space on words
  }

  if (word.type === "punct") {
    // Punctuation: no space before, space after
    return <span className="text-slate-700">{word.text}{" "}</span>;
  }

  // Determine if we need trailing space (not before punctuation)
  const trailingSpace = nextType === "punct" ? "" : " ";

  // Syllable coloring mode - use global syllable_offset for color alternation
  if (syllableColors && word.syllables && word.syllables.length > 0) {
    const silentSet = new Set(showSilent ? (word.silent_letters || []) : []);
    const offset = word.syllable_offset || 0;

    return (
      <span className="inline" data-testid={`word-${word.id}`}>
        {word.syllables.map((syl, sylIdx) => (
          <SyllableSpan
            key={sylIdx}
            syl={syl}
            sylIdx={sylIdx}
            color={syllableColors[(offset + sylIdx) % syllableColors.length]}
            silentSet={silentSet}
            highlightGraphemes={highlightGraphemes}
            wordGraphemes={word.graphemes}
            showPictograms={showPictograms}
          />
        ))}
        {trailingSpace}
      </span>
    );
  }

  // Silent letters only
  if (showSilent && word.silent_letters && word.silent_letters.length > 0) {
    const silentSet = new Set(word.silent_letters);
    return (
      <span className="inline" data-testid={`word-${word.id}`}>
        {[...word.text].map((char, idx) => (
          <span key={idx} className={silentSet.has(idx) ? "silent-letter" : "text-slate-800"}>
            {char}
          </span>
        ))}
        {trailingSpace}
      </span>
    );
  }

  // Grapheme highlighting only
  if (highlightGraphemes && word.graphemes && word.graphemes.length > 0) {
    const highlights = new Set();
    word.graphemes.forEach((g) => {
      if (highlightGraphemes.has(g.label)) {
        for (let i = g.start; i < g.end; i++) {
          highlights.add(i);
        }
      }
    });

    return (
      <span className="inline" data-testid={`word-${word.id}`}>
        {[...word.text].map((char, idx) => (
          <span
            key={idx}
            className={highlights.has(idx) ? "grapheme-highlight text-blue-700 font-bold" : "text-slate-800"}
          >
            {char}
          </span>
        ))}
        {trailingSpace}
      </span>
    );
  }

  // Plain text
  return (
    <span className="text-slate-800" data-testid={`word-${word.id}`}>
      {word.text}{trailingSpace}
    </span>
  );
}

export default function TextRenderer({ analysis, profile, showPictograms: showPictogramsProp }) {
  const rules = profile?.rules || {};

  const syllableColors = rules.syllable_coloring?.enabled
    ? (rules.syllable_coloring.colors || ["#1D4ED8", "#C2410C"])
    : null;

  const showSilent = rules.silent_letters?.enabled || false;

  const lineAlt = rules.line_alternation?.enabled
    ? (rules.line_alternation.colors || ["#EFF6FF", "#FFF7ED"])
    : null;

  const highlightGraphemes = rules.grapheme_highlight?.enabled
    ? new Set(rules.grapheme_highlight.graphemes || [])
    : null;

  // Pictograms: enabled via profile rule or explicit prop
  const showPictograms = showPictogramsProp || rules.pictograms?.enabled || false;

  if (!analysis || !analysis.sentences || analysis.sentences.length === 0) {
    return (
      <p className="text-slate-400 italic font-lexend text-xl" data-testid="text-renderer-empty">
        Saisissez un texte pour voir le resultat ici...
      </p>
    );
  }

  const lines = lineAlt
    ? analysis.sentences.map((s) => s.words)
    : [analysis.sentences.reduce((acc, s) => acc.concat(s.words), [])];

  return (
    <div
      data-testid="text-renderer"
      className="reading-area font-lexend"
    >
      {lines.map((lineWords, lineIdx) => {
        const bgColor = lineAlt ? lineAlt[lineIdx % lineAlt.length] : "transparent";

        return (
          <div
            key={lineIdx}
            className="rounded-lg px-3 py-1 -mx-3 transition-colors"
            style={{ backgroundColor: bgColor }}
            data-testid={`text-line-${lineIdx}`}
          >
            {lineWords.map((word, wordIdx) => {
              const nextWord = lineWords[wordIdx + 1];
              const nextType = nextWord?.type || null;
              return (
                <WordSpan
                  key={word.id}
                  word={word}
                  syllableColors={syllableColors}
                  showSilent={showSilent}
                  highlightGraphemes={highlightGraphemes}
                  nextType={nextType}
                  showPictograms={showPictograms}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
